import * as React from 'react';
import { Alert, Link, SubmitButton, Typography } from 'omakase';
import { Breakpoint, getResponsiveBreakpoint } from '@tock/shared/utils/responsive';
import { Form, Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import { FormActions } from '@tock/shared/components/forms/FormActions';
import { FormEmailField } from 'components/inputs/FormEmailField';
import { FormPasswordField } from 'components/inputs/FormPasswordField';
import { Grid } from 'ui';
import { IWithContextProps, withContext } from '@tock/coretypes';
import { LoginValues } from 'components/sign-in/Signin';
import { isEmail, requireFields } from '@tock/shared/utils/FormValidators';
import BaseComponent from '@tock/shared/components/BaseComponent';
import TextInput from '@tock/shared/components/inputs/DeprecatedTextInput';
import _ from 'lodash';
import queryString from '@tock/query-string';

//exported for testing
export const validate = (values: LoginValues) => {
  const errors: FormikErrors<LoginValues> = requireFields(values, 'email', 'password');
  if (!isEmail(values.email)) {
    errors.email = 'Please enter a valid email';
  }

  if (values.password.length > 256) {
    errors.password = 'Password must be less than 256 characters in length.';
    window.LOGGER?.warn('Password > 256 char login attempt');
  }

  return errors;
};

interface Props {
  email?: string;
  forgotPasswordLink?: string;
  handleChangeTabsSignUp(): void;
  hideSignupLink?: boolean;
  onSubmit(formValues: LoginValues): Promise<{ _error: string } | unknown>;
  submitText?: string;
}

interface State {
  showPassword: boolean;
}

class LoginForm extends BaseComponent<Props & IWithContextProps, State> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static defaultProps: any;

  emailInput: React.RefObject<TextInput>;
  passwordInput: React.RefObject<TextInput>;

  state = {
    showPassword: false,
  };

  constructor(props: Props & IWithContextProps) {
    super(props);
    this.emailInput = React.createRef();
    this.passwordInput = React.createRef();
  }

  componentDidMount() {
    if (getResponsiveBreakpoint() !== Breakpoint.mPortrait && this.emailInput.current) {
      this.emailInput.current.focus();
    }
  }

  handleAdvanceToFieldAfterEmail() {
    if (this.passwordInput.current) {
      this.passwordInput.current.focus();
    }
  }

  handleSubmit({ email, password }: LoginValues, actions: FormikHelpers<LoginValues>) {
    actions.setStatus('');
    const { onSubmit } = this.props;
    onSubmit({ email, password })
      .then(() => {
        actions.setSubmitting(false);
      })
      .catch((error) => {
        actions.setStatus(error._error);
        actions.setSubmitting(false);
      });
  }

  render() {
    const { submitText, email, handleChangeTabsSignUp, hideSignupLink } = this.props;

    let continueTo = '/';
    if (BROWSER) {
      continueTo = queryString.stringify(queryString.parse(window.location.search));
    }

    const forgotPasswordLink = (
      <Link href={this.props.getPath(`/forgot-password?${continueTo}`)} size="small">
        Forgot password?
      </Link>
    );

    return (
      <Formik<LoginValues>
        initialValues={{
          email: email ?? '',
          password: '',
        }}
        onSubmit={this.handleSubmit}
        enableReinitialize={false}
        validate={validate}
      >
        {(props: FormikProps<LoginValues>) => (
          <Form data-testid="login-form">
            <Grid container spacing={1}>
              {!_.isEmpty(props.errors) && props.submitCount > 0 && !props.status && (
                <Grid item xs={12}>
                  <Alert severity="error">Oops there are errors on the form</Alert>
                </Grid>
              )}
              {props.status && (
                <Grid item xs={12}>
                  <Alert severity="error">{props.status}</Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <FormEmailField
                  fullWidth
                  inputRef={this.emailInput}
                  autoComplete="email"
                  onUseSuggestion={this.handleAdvanceToFieldAfterEmail}
                />
              </Grid>
              <Grid item xs={12}>
                <FormPasswordField
                  fullWidth
                  inputRef={this.passwordInput}
                  data-testid="login-passwordfield"
                />
              </Grid>
              <Grid item xs={12}>
                {forgotPasswordLink}
              </Grid>
              <Grid item xs={12}>
                <FormActions>
                  <SubmitButton
                    data-testid="signin"
                    isSubmitting={props.isSubmitting}
                    variant="primary"
                    size="large"
                    fullWidth
                  >
                    {_.isEmpty(submitText) ? 'Log in' : submitText}
                  </SubmitButton>
                </FormActions>
              </Grid>
            </Grid>
            {!hideSignupLink ? (
              <div className="LoginForm-signUpLinkContainer">
                <Typography component="span" variant="body02">
                  New to Tock?{' '}
                  <Link onClick={handleChangeTabsSignUp} data-testid="create-account-link">
                    Create&nbsp;an&nbsp;account
                  </Link>
                </Typography>
              </div>
            ) : null}
          </Form>
        )}
      </Formik>
    );
  }
}

export default withContext(LoginForm);
